import { defineComponent as _defineComponent } from 'vue'
import { createCommentVNode as _createCommentVNode } from "vue"

import { computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import i18n from '@/i18n'
import { session } from '@/composables/useSession'
import { hasAccountRole, hasHighestWorkspaceRole } from '@/utils/roleUtils'
import { useNavigationBadgeCounts } from '@/composables/useNavigationBadgeCounts'
import { useWorkspaceList } from '@/composables/useWorkspaceList'
import { useCreateWorkspaceModal } from '@/modules/user-management/composables/useCreateWorkspaceModal'
import type { WorkspaceRole } from '@/data/roles'
import type { SidebarConfig } from '../types'

type UserManagementItemKey = 'settings.users' | 'settings.groups' | 'settings.bots'


export default /*@__PURE__*/_defineComponent({
  __name: 'UsersWorkspacesSidebar',
  emits: ["update:config"],
  setup(__props, { emit: __emit }) {

const emit = __emit

const route = useRoute()
const router = useRouter()

const currentRouteKey = computed(() => route?.name?.toString() || '')
const currentWorkspaceId = computed(() => route.params.id)
const { usersBadge, groupsBadge, botsBadge } = useNavigationBadgeCounts()
const [workspaces] = useWorkspaceList({ managedOnly: true, loadOnMount: true })
const { openModal } = useCreateWorkspaceModal()

const handleSubnavItemClick = (navItem: string, subnavItem: string) => {
  if (navItem === 'users-workspaces') {
    // Handle user management routes
    if (subnavItem === 'settings.users' || subnavItem === 'settings.groups' || subnavItem === 'settings.bots') {
      router.push({ name: subnavItem as UserManagementItemKey })
    }
    // Handle workspace setting routes - key format: "routeName:workspaceId"
    else if (subnavItem.includes(':')) {
      const [routeName, workspaceId] = subnavItem.split(':')
      router.push({ name: routeName, params: { id: workspaceId } })
    }
  }
}

const getUsersItem = computed(() => {
  if (!(hasAccountRole('ADMIN', 'MANAGER') || hasHighestWorkspaceRole('MANAGER'))) {
    return null
  }

  return {
    children: i18n.t('user_management.sidebar.account_users'),
    key: 'settings.users',
    isCurrent: currentRouteKey.value === 'settings.users',
    dataTestId: 'users-nav',
    view: 'users-workspaces',
    badge: usersBadge.value,
  }
})

const getGroupsItem = computed(() => {
  if (!(hasAccountRole('ADMIN', 'MANAGER') || hasHighestWorkspaceRole('MANAGER', 'LEAD'))) {
    return null
  }

  return {
    children: i18n.t('user_management.sidebar.groups'),
    key: 'settings.groups',
    isCurrent: currentRouteKey.value === 'settings.groups',
    dataTestId: 'groups-nav',
    view: 'users-workspaces',
    badge: groupsBadge.value,
  }
})

const getBotsItem = computed(() => {
  if (!(session.features.botQa && (hasAccountRole('ADMIN', 'MANAGER') || hasHighestWorkspaceRole('MANAGER')))) {
    return null
  }

  return {
    children: i18n.t('user_management.sidebar.bots'),
    key: 'settings.bots',
    isCurrent: currentRouteKey.value === 'settings.bots',
    dataTestId: 'bots-nav',
    view: 'users-workspaces',
    badge: botsBadge.value,
  }
})

const getAccountLevelItems = computed(() => {
  const items = []

  const usersItem = getUsersItem.value
  if (usersItem) {
    items.push(usersItem)
  }

  const groupsItem = getGroupsItem.value
  if (groupsItem) {
    items.push(groupsItem)
  }

  const botsItem = getBotsItem.value
  if (botsItem) {
    items.push(botsItem)
  }

  return items
})

const getWorkspaceSettingItems = (workspaceId: number, role: WorkspaceRole | undefined) => {
  const items = []

  // Members, General, Scorecard, Hashtags, Threshold (for ADMIN/MANAGER account or MANAGER workspace role)
  if (hasAccountRole('ADMIN', 'MANAGER') || role === 'MANAGER') {
    items.push(
      {
        children: i18n.t('settings.workspaces.settings_titles.members'),
        key: `workspace.members:${workspaceId}`,
        isCurrent: currentRouteKey.value === 'workspace.members' && currentWorkspaceId.value === workspaceId.toString(),
        dataTestId: `workspace-${workspaceId}-members`,
        view: 'users-workspaces',
      },
      {
        children: i18n.t('settings.workspaces.settings_titles.general'),
        key: `workspace.settings:${workspaceId}`,
        isCurrent:
          currentRouteKey.value === 'workspace.settings' && currentWorkspaceId.value === workspaceId.toString(),
        dataTestId: `workspace-${workspaceId}-settings`,
        view: 'users-workspaces',
      },
    )

    // Scorecard - different route based on feature flag
    if (session.features.advancedScorecardsDev) {
      items.push({
        children: i18n.t('settings.workspaces.settings_titles.scorecard'),
        key: `workspace.scorecard.moved:${workspaceId}`,
        isCurrent:
          currentRouteKey.value === 'workspace.scorecard.moved' && currentWorkspaceId.value === workspaceId.toString(),
        dataTestId: `workspace-${workspaceId}-scorecard`,
        view: 'users-workspaces',
      })
    } else {
      items.push({
        children: i18n.t('settings.workspaces.settings_titles.scorecard'),
        key: `workspace.scorecard:${workspaceId}`,
        isCurrent:
          currentRouteKey.value === 'workspace.scorecard' && currentWorkspaceId.value === workspaceId.toString(),
        dataTestId: `workspace-${workspaceId}-scorecard`,
        view: 'users-workspaces',
      })
    }

    items.push(
      {
        children: i18n.t('settings.workspaces.settings_titles.hashtags'),
        key: `workspace.hashtags:${workspaceId}`,
        isCurrent:
          currentRouteKey.value === 'workspace.hashtags' && currentWorkspaceId.value === workspaceId.toString(),
        dataTestId: `workspace-${workspaceId}-hashtags`,
        view: 'users-workspaces',
      },
      {
        children: i18n.t('settings.workspaces.settings_titles.threshold'),
        key: `workspace.threshold:${workspaceId}`,
        isCurrent:
          currentRouteKey.value === 'workspace.threshold' && currentWorkspaceId.value === workspaceId.toString(),
        dataTestId: `workspace-${workspaceId}-threshold`,
        view: 'users-workspaces',
      },
    )
  }

  // Assignments
  items.push({
    children: i18n.t('settings.workspaces.settings_titles.assignments'),
    key: `workspace.assignments:${workspaceId}`,
    isCurrent: currentRouteKey.value === 'workspace.assignments' && currentWorkspaceId.value === workspaceId.toString(),
    dataTestId: `workspace-${workspaceId}-assignments`,
    view: 'users-workspaces',
  })

  // Connections, Calibration (for ADMIN/MANAGER account or MANAGER workspace role)
  if (hasAccountRole('ADMIN', 'MANAGER') || role === 'MANAGER') {
    items.push(
      {
        children: i18n.t('settings.workspaces.settings_titles.connections'),
        key: `workspace.connections:${workspaceId}`,
        isCurrent:
          currentRouteKey.value === 'workspace.connections' && currentWorkspaceId.value === workspaceId.toString(),
        dataTestId: `workspace-${workspaceId}-connections`,
        view: 'users-workspaces',
      },
      {
        children: i18n.t('settings.workspaces.settings_titles.calibration'),
        key: `workspace.calibration:${workspaceId}`,
        isCurrent:
          currentRouteKey.value === 'workspace.calibration' && currentWorkspaceId.value === workspaceId.toString(),
        dataTestId: `workspace-${workspaceId}-calibration`,
        view: 'users-workspaces',
      },
    )
  }

  return items
}

const getWorkspaceGroups = computed(() => {
  return workspaces.value.map((workspace) => {
    const isCurrentWorkspace = currentWorkspaceId.value === workspace.id.toString()
    return {
      key: `workspace-${workspace.id}`,
      legend: workspace.name,
      legendData: {
        text: workspace.name,
        color: workspace.color,
      },
      isCollapsible: true,
      defaultCollapsed: !isCurrentWorkspace,
      dataTestId: `workspace-${workspace.id}-group`,
      items: getWorkspaceSettingItems(workspace.id, workspace.role),
      view: 'users-workspaces',
    }
  })
})

const handleSubnavItemActionClick = () => {
  // No-op for now
}

const handleGroupActionClick = () => {
  // No-op for now
}

const actionMenu = computed(() => {
  if (!hasAccountRole('ADMIN', 'MANAGER')) {
    return {
      tooltip: '',
      onClick: () => {
        // No-op for now
      },
      menuItems: [],
    }
  }

  return {
    tooltip: i18n.t('user_management.create_workspace.title'),
    onClick: openModal,
    menuItems: [],
  }
})

const config = computed(
  (): SidebarConfig => ({
    title: i18n.t('user_management.sidebar.title_users_workspaces_v2'),
    items: [...getAccountLevelItems.value, ...getWorkspaceGroups.value],
    action: actionMenu.value,
    handleSubnavItemClick,
    handleSubnavItemActionClick,
    handleGroupActionClick,
  }),
)

// Emit initial config and watch for changes
onMounted(() => {
  emit('update:config', config.value)
})

// Watch for changes and emit updates
watch(config, (newConfig) => {
  emit('update:config', newConfig)
})

return (_ctx: any,_cache: any) => {
  return _createCommentVNode(" Renderless component ")
}
}

})